<template>
  <div>
    <v-sheet class="sticky">
      <v-btn-toggle
        v-if="showProgramToggle"
        v-model="programView"
        dense
        mandatory
        color="primary"
        @change="loadRoster"
      >
        <v-btn small value="SEIT">SEIT</v-btn>
        <v-btn small value="RS">Related Services</v-btn>
      </v-btn-toggle>
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            clearable
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="2">
          <download-csv-btn
            :headers="_headers"
            :items="deduplicatedAndFilteredAndSortedList"
            :fileName="'ClientRoster.csv'"
          ></download-csv-btn
        ></v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="auto"
          ><custom-workviews
            :programView="programView"
            @applyDefault="applyDefault"
          ></custom-workviews>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn small rounded outlined @click="applyDefault"
            >Reset to Default</v-btn
          ></v-col
        ><v-col cols="12" sm="auto">
          <client-roster-display
            :columns="_availableHeaders"
          ></client-roster-display
        ></v-col>
        <v-col cols="12" sm="auto">
          <client-roster-filters
            :programView="programView"
          ></client-roster-filters
        ></v-col>
      </v-row>
      <v-data-table
        :headers="_headers"
        :items="deduplicatedAndFilteredList"
        :search="search"
        :loading="loading"
        :items-per-page="10"
        dense
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        item-key="uniqueId"
      >
        <template v-for="tab in tabs" v-slot:[`item.${tab.name}`]="{ item }">
          <div
            :key="tab.id"
            class="text-decoration-underline indigo--text"
            style="cursor: pointer"
            @click="togglePanel(tab, item[tab.param])"
          >
            {{ item[tab.name] }}
          </div>
        </template>
        <template v-slot:[`item.FADSubmitted`]="{ item }">
          <span
            v-if="Boolean(item.firstAttendDateSubmittedAt)"
            class="text-body-2 font-weight-light"
            >{{ item.formattedFADSubmitted }}</span
          >
          <v-tooltip v-else-if="item.isContinuingRegistration" bottom>
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                class="text-body-2 font-weight-light grey--text"
                >Not required</span
              >
            </template>
            <span>
              Continues the previous service plan with the same mandate. No FAD
              submission needed.
            </span>
          </v-tooltip>
          <v-checkbox
            v-else
            :key="`${item.uniqueId}-${fadCheckboxKey}`"
            :value="Boolean(item.firstAttendDateSubmittedAt)"
            x-small
            :disabled="!Boolean(item.formattedFAD)"
            @change="setFADSubmitted(item, $event)"
          ></v-checkbox>
        </template>
      </v-data-table>
    </v-sheet>
    <client-roster-sidebar
      v-if="Boolean(activeComponent)"
      :activeComponent="activeComponent"
      :schoolId="Boolean(selectedSchoolId) ? selectedSchoolId : null"
      :clientId="Boolean(selectedClientId) ? selectedClientId : null"
      :districtAdminId="
        Boolean(selectedDistrictAdminId) ? selectedDistrictAdminId : null
      "
      :providerId="Boolean(selectedProviderId) ? selectedProviderId : null"
      @closePanel="togglePanel()"
    ></client-roster-sidebar>
  </div>
</template>

<script>
import ClientRosterFilters from "./client-roster-filters.vue";
import ClientRosterDisplay from "./client-roster-display.vue";
import DownloadCsvBtn from "../Shared/download-csv-btn.vue";
import ClientRosterSidebar from "./client-roster-sidebar.vue";
import CustomWorkviews from "./custom-workviews";
import { sortByDate, dynamicSort } from "../../utils/sort-utils";
import { deduplicate } from "../../utils/data-table-utils";
export default {
  components: {
    ClientRosterFilters,
    ClientRosterDisplay,
    DownloadCsvBtn,
    ClientRosterSidebar,
    CustomWorkviews,
  },
  data: function () {
    return {
      search: "",
      loading: false,
      activeComponent: null,
      selectedSchoolId: null,
      selectedClientId: null,
      selectedDistrictAdminId: null,
      selectedProviderId: null,
      fadCheckboxKey: 0,
      programView: "SEIT",
      sortBy: [],
      sortDesc: [],
    };
  },
  async mounted() {
    if (this.isSEITAdmin) {
      this.programView = "SEIT";
    } else if (this.isRSAdmin) {
      this.programView = "RS";
    }
    await this.loadRoster();
  },
  beforeDestroy() {
    this.$store.dispatch("plansModule/cancelCurrentRosterRequest");
  },
  computed: {
    isSEITAdmin() {
      return this.$store.getters["securityModule/isSEITAdmin"];
    },
    isRSAdmin() {
      return this.$store.getters["securityModule/isRSAdmin"];
    },
    showProgramToggle() {
      return this.isSEITAdmin && this.isRSAdmin;
    },
    headers() {
      return [
        {
          text: "Client Name",
          value: "clientName",
          default: true,
          fixed: true,
          width: "150px",
          class: "sticky-header first-col",
          cellClass: "sticky-column first-col",
        },
        {
          text: "DOB",
          value: "formattedDob",
          sortable: false,
          default: true,
        },
        {
          text: "OSIS",
          value: "formattedOsis",
          sortable: false,
          default: true,
        },
        {
          text: "ZIP",
          value: "zipCode",
        },
        {
          text: "District Admin",
          value: "districtAdminFullName",
          hide: this.programView === "RS",
        },
        {
          text: "District",
          value: "districtId",
          sortable: false,
          default: true,
        },
        {
          text: "School",
          value: "schoolName",
          default: true,
        },
        {
          text: "Team",
          value: "teamName",
          hide: this.programView === "RS",
          default: this.programView === "SEIT", // SEIT only
        },
        {
          text: "Program",
          value: "programCode",
          sortable: false,
          hide: this.programView === "SEIT",
          default: this.programView === "RS", // Related Services only
        },
        {
          text: "Service",
          value: "serviceCode",
          sortable: false,
          hide: this.programView === "SEIT",
          default: this.programView === "RS", // Related Services only
        },
        {
          text: "Plan Type",
          value: "planType",
          sortable: false,
        },
        {
          text: "Next Meeting Scheduled",
          value: "formattedNextMeetingScheduledDate",
          sort: (a, b) => sortByDate(a, b),
        },
        {
          text: "Plan Approval Start",
          value: "formattedServicePlanStartDate",
          sort: (a, b) => sortByDate(a, b),
          default: true,
        },
        {
          text: "Plan Approval End",
          value: "formattedServicePlanEndDate",
          sort: (a, b) => sortByDate(a, b),
          default: true,
        },
        {
          text: "Mandate",
          value: "formattedMandate",
          sortable: false,
          default: true,
        },
        {
          text: "Group Size",
          value: "groupSize",
          default: true,
        },
        {
          text: "10/12 Month",
          value: "tenTwelveMonth",
          default: true,
        },
        {
          text: "Language",
          value: "language",
          default: true,
        },
        {
          text: "Location",
          value: "location",
          default: this.programView === "RS", // Related Services only
        },
        {
          text: "Semester",
          value: "semesterCode",
          default: true,
        },
        {
          text: "Provider",
          value: "providerFullName",
          default: true,
        },
        {
          text: "Assignment Approval Start",
          value: "formattedApprovalStartDate",
          sort: (a, b) => sortByDate(a, b),
          default: true,
        },
        {
          text: "Assignment Approval End",
          value: "formattedApprovalEndDate",
          sort: (a, b) => sortByDate(a, b),
          default: true,
        },
        {
          text: "FAD",
          value: "formattedFAD",
          sort: (a, b) => sortByDate(a, b),
          default: true,
        },
        {
          text: "FAD Submitted",
          value: "FADSubmitted",
          sortable: false,
        },
      ];
    },
    tabs() {
      return [
        {
          id: 1,
          name: "schoolName",
          param: "schoolId",
          component: "SchoolDetails",
        },
        {
          id: 2,
          name: "clientName",
          param: "clientId",
          component: "ClientDetails",
        },
        {
          id: 3,
          name: "districtAdminFullName",
          param: "districtAdminId",
          component: "DistrictAdminForm",
        },
        {
          id: 4,
          name: "providerFullName",
          param: "providerId",
          component: "ProviderDetails",
        },
      ];
    },
    filteredRoster() {
      return this.$store.getters["plansModule/getFilteredRoster"];
    },
    displayColumns() {
      return this.$store.state.plansModule.displayColumns;
    },
    _availableHeaders() {
      return this.headers.filter((h) => !h.hide);
    },
    _headers() {
      return this.headers.filter((h) => this.displayColumns.includes(h.value));
    },
    defaultHeaders() {
      return this.headers.filter((x) => x.default);
    },
    deduplicatedAndFilteredList() {
      return deduplicate(this.filteredRoster, this.displayColumns);
    },
    deduplicatedAndFilteredAndSortedList() {
      return this.deduplicatedAndFilteredList.sort(
        dynamicSort(this.sortBy, this.sortDesc)
      );
    },
  },
  methods: {
    async loadRoster() {
      this.loading = true;
      this.activeComponent = null;
      this.selectedSchoolId = null;
      this.selectedClientId = null;
      this.selectedDistrictAdminId = null;
      this.selectedProviderId = null;
      await this.$store.dispatch("plansModule/clearClientRoster");

      if (this.programView == "SEIT") {
        await this.$store.dispatch("plansModule/getClientRoster", {
          programIds: [6],
        });
      } else if (this.programView == "RS") {
        await this.$store.dispatch("plansModule/getClientRoster", {
          programIds: [3, 8, 18],
        });
      }
      // if no custom workview selected || selected workview doesn't apply to program view -> apply default display + current semester filter
      const selected = this.$store.state.plansModule.selectedCustomWorkview;
      if (
        !Boolean(selected) ||
        (Boolean(selected.programView) &&
          selected.programView != this.programView)
      ) {
        this.applyDefault();
      }
      this.loading = false;
    },
    applyDefault() {
      this.$store.dispatch("plansModule/clearSelectedCustomWorkview");
      this.$store.dispatch("plansModule/clearFilters");
      // default view to apply semester filter to current semester
      var currentSemesterCode =
        this.$store.getters["optionsModule/getCurrentSemester"]?.semesterCode;
      this.$store.dispatch("plansModule/setFilters", {
        filterName: "semesterFilter",
        filterValue: { id: currentSemesterCode, text: currentSemesterCode },
      });
      // default view to hide certain columns
      this.$store.dispatch(
        "plansModule/setDisplayColumns",
        this.defaultHeaders.map((x) => x.value)
      );
    },
    togglePanel(item, id) {
      if (Boolean(item)) {
        this.activeComponent = item.component;
        switch (item.param) {
          case "schoolId":
            this.selectedSchoolId = id;
          case "clientId":
            this.selectedClientId = id;
          case "districtAdminId":
            this.selectedDistrictAdminId = id;
          case "providerId":
            this.selectedProviderId = id;
        }
      } else {
        this.activeComponent = null;
      }
    },
    async setFADSubmitted(item, event) {
      this.$store.commit("uxModule/setShowLoader", true);
      let message;
      if (this.programView == "SEIT") {
        message = await this.$store.dispatch("plansModule/setFADSubmitted", {
          servicePlanId: item.servicePlanId,
          semesterId: item.semesterId,
          submitted: event,
        });
      } else if (this.programView == "RS") {
        message = await this.$store.dispatch(
          "plansModule/setAssignmentFADSubmitted",
          {
            assignmentId: item.assignmentId,
            submitted: event,
          }
        );
      }
      if (message) {
        // backend didn't save it, so re-render the checkbox to drop the click
        this.fadCheckboxKey++;
        this.$store.commit("uxModule/setSnackbarMsg", message);
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
      this.$store.commit("uxModule/setShowLoader", false);
    },
  },
};
</script>
<style scoped>
.sticky {
  background-color: white;
  position: sticky;
  top: 60px;
  z-index: 1;
}
.v-data-table ::v-deep .v-data-table__wrapper {
  max-height: 750px;
  overflow-y: auto;
  overflow-x: auto;
}

.v-data-table ::v-deep thead th {
  position: sticky !important;
  top: 0;
  z-index: 1;
  background: white !important;
}
.v-data-table ::v-deep .sticky-header,
.v-data-table ::v-deep .sticky-column {
  position: sticky !important;
  background-color: white !important;
}

.v-data-table ::v-deep .sticky-header {
  z-index: 4 !important;
}
.v-data-table ::v-deep .first-col {
  left: 0;
}
</style>
